import { useCallback, useRef } from "react";

const SHAKE_DURATION_IN_SECONDS = 0.82;

/**
 * Returns a ref and a shake() function
 * Attach the ref to any HTMLElement, then call shake() to make that element play a left-to-right shake animation
 */
export function useShake() {
  const shakeableRef = useRef<HTMLElement | undefined>(null);

  const shake = useCallback(() => {
    if (shakeableRef.current) {
      // Remove existing animation if it exists
      shakeableRef.current.style.animation = "";

      // Force reflow to ensure the style change takes effect
      void shakeableRef.current.offsetHeight;

      // Define keyframes if not already present
      const keyframeId = "shake-animation";
      if (!document.querySelector(`style[data-shake-keyframes="${keyframeId}"]`)) {
        const style = document.createElement("style");
        style.dataset.shakeKeyframes = keyframeId;
        style.textContent = `
          @keyframes ${keyframeId} {
            0% { transform: translate3d(0, 0, 0); }
            10%, 90% { transform: translate3d(-1px, 0, 0); }
            20%, 80% { transform: translate3d(1px, 0, 0); }
            30%, 50%, 70% { transform: translate3d(-2px, 0, 0); }
            40%, 60% { transform: translate3d(2px, 0, 0); }
            100% { transform: translate3d(0, 0, 0); }
          }
        `;
        document.head.append(style);
      }

      // Apply performance optimizations
      shakeableRef.current.style.backfaceVisibility = "hidden";
      shakeableRef.current.style.perspective = "1000px";
      shakeableRef.current.style.willChange = "transform";

      // Apply shake animation with cubic-bezier for realistic easing
      shakeableRef.current.style.animation = `${keyframeId} ${SHAKE_DURATION_IN_SECONDS}s cubic-bezier(.36,.07,.19,.97) both`;

      // Clean up animation after it completes
      const cleanup = () => {
        if (shakeableRef.current) {
          shakeableRef.current.style.animation = "";
          shakeableRef.current.style.willChange = "auto";
        }
      };

      setTimeout(cleanup, SHAKE_DURATION_IN_SECONDS * 1000);
    }
  }, []);

  return { ref: shakeableRef, shake };
}
